import React, { useState, useContext, useRef, useEffect } from "react";
import "./index.css";
import unn from "../../data/unn.png";
import { ValueContext } from "../../Context";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE_URL, authFetch } from "../../config/api";
import { useOffline } from "../../hooks/useOffline";
import {
  saveOfflineCredentials,
  verifyOfflinePin,
  hasOfflineCredentials,
  getOfflineEmail,
} from "../../utils/offlineAuth";

const REASON_MESSAGES = {
  inactivity: "You were signed out due to 10 minutes of inactivity.",
  duplicate_tab:
    "This site is already open in another tab. Only one tab is allowed.",
};

// steps: "email" | "otp" | "password" | "set_pin" | "offline_pin"
function Admin() {
  const navigate = useNavigate();
  const isOffline = useOffline();
  const { setAlert } = useContext(ValueContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const otpRefs = useRef([]);

  const [step, setStep] = useState("email");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(false);

  // pending login payload — held while user sets their PIN
  const pendingLogin = useRef(null);

  // ── show redirect reason ────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlReason = params.get("reason");
    const storageReason = localStorage.getItem("logout_reason");
    const reason = urlReason || storageReason;
    if (reason) {
      localStorage.removeItem("logout_reason");
      window.history.replaceState({}, "", "/admin");
      setAlert(
        true,
        REASON_MESSAGES[reason] || "Your session has ended.",
        "warning",
      );
    }
  }, [setAlert]);

  // ── if offline, go straight to offline pin step ─────────────────────────────
  useEffect(() => {
    if (isOffline && step === "email") {
      if (hasOfflineCredentials()) {
        setEmail(getOfflineEmail());
        setStep("offline_pin");
      } else {
        setAlert(
          true,
          "You are offline and no offline PIN has been set. Please connect to the internet to log in.",
          "warning",
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOffline]);

  // ── OTP helpers ─────────────────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
    if (digit && index === 5 && next.every((d) => d !== ""))
      submitOtp(next.join(""));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const next = Array(6).fill("");
    pasted.split("").forEach((d, i) => {
      next[i] = d;
    });
    setOtp(next);
    otpRefs.current[Math.min(pasted.length - 1, 5)]?.focus();
    if (pasted.length === 6) submitOtp(pasted);
  };

  const submitOtp = (otpValue) => {
    if (!loading) doLogin({ otpValue });
  };

  // ── step 1: email submit ────────────────────────────────────────────────────
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return setAlert(true, "Please enter your email.", "error");
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/users/request-otp`, {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      const json = await res.json();
      if (res.status === 400 && json.error?.includes("Super admin")) {
        setIsSuperAdmin(true);
        setStep("password");
      } else if (res.status === 404) {
        setAlert(
          true,
          json.error || "No account found with that email.",
          "error",
        );
      } else if (!res.ok) {
        setAlert(true, json.error || "Something went wrong.", "error");
      } else {
        setStep("otp");
        setAlert(true, "OTP sent to your email.", "success");
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      }
    } catch {
      setAlert(true, "Server error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── step 2: complete login (OTP or password) ────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    doLogin({});
  };

  const doLogin = async ({ otpValue } = {}) => {
    const otpString = otpValue ?? otp.join("");
    if (isSuperAdmin && !password)
      return setAlert(true, "Please enter your password.", "error");
    if (!isSuperAdmin && otpString.length < 6)
      return setAlert(true, "Please enter the full OTP.", "error");
    setLoading(true);
    try {
      const body = isSuperAdmin
        ? { email, password }
        : { email, otp: otpString };
      const res = await authFetch(`${API_BASE_URL}/api/users/login`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        setAlert(true, json.error || "Login failed.", "error");
        if (!isSuperAdmin) {
          setOtp(Array(6).fill(""));
          setTimeout(() => otpRefs.current[0]?.focus(), 50);
        }
      } else {
        // Store token info
        pendingLogin.current = {
          token: json.token,
          role: json.role,
          name: json.name,
        };
        // If PIN already set, just log in — otherwise prompt to set one
        if (hasOfflineCredentials()) {
          commitLogin(json.token, json.role, json.name);
        } else {
          // First time — ask to set an offline PIN
          setStep("set_pin");
        }
      }
    } catch {
      setAlert(true, "Server error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── step 3: set offline PIN ─────────────────────────────────────────────────
  const handleSetPin = async (e) => {
    e.preventDefault();
    if (pin.length < 4)
      return setAlert(true, "PIN must be at least 4 characters.", "error");
    if (pin !== confirmPin)
      return setAlert(true, "PINs do not match.", "error");
    setLoading(true);
    const { token, role, name } = pendingLogin.current;
    await saveOfflineCredentials(email, pin, role, name);
    commitLogin(token, role, name);
  };

  const skipSetPin = () => {
    const { token, role, name } = pendingLogin.current;
    commitLogin(token, role, name);
  };

  const commitLogin = (token, role, name) => {
    localStorage.setItem("token", token);
    localStorage.setItem("role", role);
    localStorage.setItem("name", name);
    navigate("/");
  };

  // ── offline PIN login ───────────────────────────────────────────────────────
  const handleOfflineLogin = async (e) => {
    e.preventDefault();
    if (!pin) return setAlert(true, "Please enter your offline PIN.", "error");
    setLoading(true);
    const creds = await verifyOfflinePin(email, pin);
    setLoading(false);
    if (!creds) {
      setAlert(true, "Incorrect PIN. Please try again.", "error");
      setPin("");
      return;
    }
    // Reuse existing token if still valid, else block — can't get a new one offline
    const existingToken = localStorage.getItem("token");
    if (!existingToken) {
      setAlert(
        true,
        "Your session has fully expired. Please connect to the internet to log in again.",
        "error",
      );
      return;
    }
    localStorage.setItem("role", creds.role);
    localStorage.setItem("name", creds.name);
    navigate("/");
  };

  const handleBack = () => {
    setStep("email");
    setOtp(Array(6).fill(""));
    setPassword("");
    setPin("");
    setConfirmPin("");
    setIsSuperAdmin(false);
    pendingLogin.current = null;
  };

  return (
    <div className="admin_login">
      <div className="admin_login_logos">
        <div className="admin_login_logo">
          <img src={unn} alt="unn_logo" />
        </div>
      </div>
      <div className="admin_login_title">
        <h1>Faculty of Pharmaceutical Sciences</h1>
        <h2>University of Nigeria Nsukka</h2>
      </div>

      <h2>Sign In</h2>

      {/* ── offline badge ── */}
      {isOffline && (
        <div className="admin_offline_badge">
          📶 You are offline — using offline access
        </div>
      )}

      {/* ── email step ── */}
      {step === "email" && !isOffline && (
        <form className="admin_login_form" onSubmit={handleEmailSubmit}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          <button type="submit" disabled={loading}>
            {loading ? "Please wait..." : "Continue"}
          </button>
        </form>
      )}

      {/* ── OTP step ── */}
      {step === "otp" && (
        <form className="admin_login_form" onSubmit={handleLogin}>
          <p className="admin_login_hint">
            Enter the 6-digit OTP sent to <strong>{email}</strong>
          </p>
          <div className="otp_boxes">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => (otpRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(i, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(i, e)}
                onPaste={i === 0 ? handleOtpPaste : undefined}
                className="otp_box"
                autoComplete="one-time-code"
                disabled={loading}
              />
            ))}
          </div>
          {loading && (
            <p className="admin_login_hint" style={{ marginTop: "1rem" }}>
              Signing in...
            </p>
          )}
          <button
            type="button"
            className="admin_login_back"
            onClick={handleBack}
            disabled={loading}
          >
            Back
          </button>
        </form>
      )}

      {/* ── password step (super admin) ── */}
      {step === "password" && (
        <form className="admin_login_form" onSubmit={handleLogin}>
          <input
            type="email"
            value={email}
            readOnly
            style={{ color: "#94a3b8" }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <button type="submit" disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
          <button
            type="button"
            className="admin_login_back"
            onClick={handleBack}
          >
            Back
          </button>
          <Link to="/forgot-password" className="admin_login_forgot">
            Forgot password?
          </Link>
        </form>
      )}

      {/* ── set offline PIN (first login) ── */}
      {step === "set_pin" && (
        <form className="admin_login_form" onSubmit={handleSetPin}>
          <p className="admin_login_hint">
            Set an <strong>offline PIN</strong> so you can access the app
            without internet. Use at least 4 characters.
          </p>
          <input
            type="password"
            placeholder="Create offline PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoComplete="new-password"
          />
          <input
            type="password"
            placeholder="Confirm offline PIN"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value)}
            autoComplete="new-password"
          />
          <button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save PIN & Continue"}
          </button>
          <button
            type="button"
            className="admin_login_back"
            onClick={skipSetPin}
          >
            Skip for now
          </button>
        </form>
      )}

      {/* ── offline PIN login ── */}
      {step === "offline_pin" && (
        <form className="admin_login_form" onSubmit={handleOfflineLogin}>
          <p className="admin_login_hint">
            Enter your offline PIN to continue as <strong>{email}</strong>
          </p>
          <input
            type="password"
            placeholder="Offline PIN"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoComplete="current-password"
            autoFocus
          />
          <button type="submit" disabled={loading}>
            {loading ? "Verifying..." : "Sign In"}
          </button>
        </form>
      )}
    </div>
  );
}

export default Admin;
